/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component, createRef } from 'react';
import { string, func, shape, bool, object, instanceOf } from 'prop-types';
import { NavLink, withRouter } from 'react-router-dom';
import { connect } from 'react-redux';

import p from 'polyglot/polyglot';

import { createUrl } from 'utils/urlHelper';

import { SIGNUP_STATUS } from 'reducers/user';

import { logout as logoutAction } from 'actions/auth';
import { showLoginModal as showLoginModalAction } from 'actions/login';

import SiteLogo from 'components/SiteLogo/SiteLogo';
import KstingIcon from 'gfx/vector/ksting-icon.svg';
import KstingIconLight from 'gfx/vector/ksting-icon-light.svg';
import Hamburger from 'components/Hamburger/Hamburger';

import Menu from './Menu/Menu';
import EditModeMenu from './EditModeMenu/EditModeMenu';
import LikesButton from './LikesButton/LikesButton';
import ShortlistButton from './ShortlistButton/ShortlistButton';
import MailIcon from 'gfx/vector/mail-icon.svg';
import Button from 'components/Button/Button';
import Counter from 'components/Counter/Counter';

import classNames from 'clsx';

import styles from './header.scss';

/**
 * This is the Header component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 * @author Niklas Rosdahl <niklas.rosdahl@vntrs.com>
 * @author Pelle Lundgren <pelle.lundgren@vntrs.com>
 */
export class Header extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    location: object.isRequired,
    auth: shape({
      isAuthenticated: bool.isRequired,
      token: string,
    }).isRequired,
    user: shape({
      status: string,
    }).isRequired,
    showLoginModal: func.isRequired,
    logout: func.isRequired,
    shortlistRef: shape({ current: instanceOf(Element) }).isRequired,
  };

  constructor() {
    super();

    this.menuElem = createRef();
  }

  state = {
    menuOpen: false,
    menuTransparent: false,
  };

  componentDidMount() {
    const { location } = this.props;
    if (location.pathname === '/') {
      this.setState(
        {
          menuTransparent: true,
        },
        () => window.addEventListener('scroll', this.handleScroll)
      );
    }
  }

  componentDidUpdate(prevProps) {
    const { menuOpen, menuTransparent } = this.state;
    const { location } = prevProps;
    const { location: nextLocation } = this.props;

    // Make sure the header is only transparent on home page. Refactor later.

    if (
      !menuTransparent &&
      location !== nextLocation &&
      nextLocation.pathname === '/'
    ) {
      this.setState(
        {
          menuTransparent: true,
        },
        window.addEventListener('scroll', this.handleScroll)
      );
    }

    if (menuTransparent && location.pathname !== nextLocation.pathname) {
      this.setState(
        {
          menuTransparent: false,
        },
        window.removeEventListener('scroll', this.handleScroll)
      );
    }

    if (menuOpen && location !== nextLocation) {
      this.setState({
        menuOpen: false,
      });
    }
  }

  componentWillUnmount() {
    const { location } = this.props;
    if (location.pathname === '/') {
      window.removeEventListener('scroll', this.handleScroll);
    }
  }

  handleScroll = () => {
    if (window.scrollY > 200) {
      this.setState({
        menuTransparent: false,
      });
    } else if (
      window.scrollY < 200 &&
      (location.pathname === '/en/' ||
        location.pathname === '/sv/' ||
        location.pathname === '/')
    ) {
      this.setState({
        menuTransparent: true,
      });
    }
  };

  getHomeUrl = isApproved => {
    const { user } = this.props;

    if (isApproved) {
      if (user.is_client) {
        return createUrl('talents');
      }

      return createUrl('account');
    }

    return createUrl('home');
  };

  onClickLogin = event => {
    event.preventDefault();

    const { showLoginModal } = this.props;

    showLoginModal();
  };

  onClickLogout = event => {
    event.preventDefault();
    const { logout } = this.props;

    logout();
    window.location.href = createUrl('home');
  };

  onToggleNavigation = e => {
    e.preventDefault();

    const { menuOpen } = this.state;

    this.setState({ menuOpen: !menuOpen });
  };

  /**
   * Render Header.
   *
   * @return {Object}
   */
  render() {
    const { menuOpen, menuTransparent } = this.state;
    const {
      auth: { isAuthenticated },
      user: {
        status,
        is_talent,
        is_client,
        show_edit_mode,
        has_new_inbox_content,
        notifications,
      },
      shortlistRef,
    } = this.props;

    const isApproved = status === SIGNUP_STATUS.APPLICATION_APPROVED;
    const siteLogoUrl = this.getHomeUrl(isApproved);

    const hasShortListButtons =
      !isAuthenticated || (isAuthenticated && is_client);

    return (
      <React.Fragment>
        <div
          className={classNames(styles.wrapper, {
            [styles.transparent]: menuTransparent,
          })}
        >
          <div className={styles.left}>
            <Hamburger
              innerRef={this.menuElem}
              active={menuOpen}
              className={classNames(styles.hamburger, {
                [styles.light]: menuTransparent,
              })}
              onClick={this.onToggleNavigation}
            />
            {!isAuthenticated && (
              <Button
                small
                white={menuTransparent}
                dark={!menuTransparent}
                ghost
                className={styles.loginButton}
                onClick={this.onClickLogin}
              >
                {p.t('header.login_button')}
              </Button>
            )}
            {isAuthenticated && is_talent && (
              <Button
                className={styles.logoutButton}
                onClick={this.onClickLogout}
                small
                ghost
                dark
              >
                {p.t('header.menu.logout')}
              </Button>
            )}
          </div>
          <div className={styles.center}>
            <NavLink to={siteLogoUrl} className={styles.logo}>
              {menuTransparent && (
                <SiteLogo light className={styles.logoDesktop} />
              )}
              {!menuTransparent && (
                <SiteLogo dark className={styles.logoDesktop} />
              )}
              {menuTransparent && (
                <KstingIconLight className={styles.logoMobile} />
              )}
              {!menuTransparent && <KstingIcon className={styles.logoMobile} />}
            </NavLink>
          </div>
          <div className={styles.right}>
            {isAuthenticated && show_edit_mode && <EditModeMenu />}
            {hasShortListButtons && (
              <>
                {menuTransparent && <LikesButton white />}
                {!menuTransparent && <LikesButton />}
                {menuTransparent && (
                  <ShortlistButton white innerRef={shortlistRef} />
                )}
                {!menuTransparent && (
                  <ShortlistButton innerRef={shortlistRef} />
                )}
              </>
            )}
            {isAuthenticated && isApproved && (
              <NavLink
                to={createUrl('inbox')}
                exact
                className={styles.inboxButton}
                activeClassName={styles.selected}
              >
                <MailIcon className={styles.mail} />
                {has_new_inbox_content && (
                  <Counter accent count={notifications?.length - 1} />
                )}
              </NavLink>
            )}
          </div>
        </div>
        <Menu
          active={menuOpen}
          parentElem={this.menuElem}
          onClose={this.onToggleNavigation}
        />
      </React.Fragment>
    );
  }
}

export default withRouter(
  connect(({ auth, user }) => ({ auth, user }), {
    logout: logoutAction,
    showLoginModal: showLoginModalAction,
  })(Header)
);
